import React, { useCallback, useEffect, useState } from "react";
import { PostContentHead } from "@/components/modules/Post/Content/PostContentHead";
import { PostSubjectIntroducing } from "@/components/modules/Post/Content/PostSubjectIntroducing";
import { ReporterSocials } from "@/components/modules/Post/Widgets/ReporterSocials";
import { PostPageProps } from "@/types/modules/post/components";
import { useParsedContent } from "@/hooks/useParsedContent";
import { Text } from "@/components/ui-kit/Text";
import styles from "@/styles/post.module.css";
import { PostPaywall } from "@/components/modules/Post/Paywall/PostPaywall";
import { DataRequestType } from "@/core/types/dataRequestsTypes";
import ProvidePostContentById from "@/connectors/fcWordpress/thunks/ProvidePostContentById";
import { wrapper } from "@/core/store/store";
import { unlockPost } from "@/core/store/app/AppSlice";
import useUserProvider from "@/core/dataProviders/providers/auth/useUserProvider";
import { PostUnlockedPaywall } from "@/components/modules/Post/Paywall/PostUnlockedPaywall";
import { useRouter } from "next-translate-routes";
import { SuccessDialogModalWindow } from "@/components/modules/Post/SuccessDialogModalWindow";
import useUserSubscriptionProvider from "@/core/dataProviders/providers/auth/useUserSubscriptionProvider";
import { pushToDataLayer } from "@/utils/pushToDataLayer";
import { Loader } from "@/components/ui-kit/Loader";
import { useRemplib } from "@/hooks/useRemplib";

export const PostContent: React.FC<PostPageProps> = ({ post }) => {
  const [user] = useUserProvider();
  const [subscriptions, subscriptionReqState] = useUserSubscriptionProvider();
  const [parsedContent, allImageSources] = useParsedContent(post.content ?? "", true, post);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isLoadingArticle, setIsLoadingArticle] = useState(false);
  const [isLoginDialogVisible, setIsLoginDialogVisible] = useState(false);
  const { store } = wrapper.useWrappedStore({});
  const router = useRouter();
  useRemplib(post);

  const isUserLoggedInAndUnlocked =
    user &&
    user.user_meta.unlocked_posts?.includes(post.id.toString()) &&
    !subscriptions?.length &&
    !isLoadingArticle &&
    isUnlocked;

  const providePostContent = useCallback(
    async (hash?: string, ast?: string) => {
      await ProvidePostContentById(
        {
          sign: {
            id: post.id?.toString(),
            hash: hash,
            ast: ast,
          },
          language: process.env.NEXT_PUBLIC_DEFAULT_API_LANGUAGE_CODE!,
          type: DataRequestType.POST_CONTENT_BY_ID,
        },
        store.dispatch
      );
    },
    [post.id, store.dispatch]
  );

  useEffect(() => {
    if (!!subscriptions?.length && post.id) {
      providePostContent().then(() => {
        setShowPaywall(false);
      });
    }
  }, [providePostContent, subscriptions, post]);

  // If there is scrollY query param in URL, we scroll down to paywall element with offset.
  // This is due to user log in via blocker, so he can continue reading where he left.
  useEffect(() => {
    const scrollY = typeof router.query.scrollY === "string" ? router.query.scrollY : null;
    const unlockedContentAnchor = document.getElementById("remp_lock_anchor");
    const lockedContentAnchor = document.getElementById("remp_content_locked");
    const elementToScroll = unlockedContentAnchor || lockedContentAnchor;

    if (!scrollY || !elementToScroll) {
      return;
    }

    // create new element with offset that we will scroll into view
    // this is because we need a little offset from top when we scroll
    const offsetElement = document.createElement("div");
    offsetElement.id = "continue-reading-el";
    offsetElement.style.position = "relative";
    offsetElement.style.top = "-100px";
    elementToScroll.parentElement?.insertBefore(offsetElement, elementToScroll);

    // scroll down
    setTimeout(() => {
      offsetElement.scrollIntoView();
    }, 250);

    // remove scrollY query param from URL
    delete router.query["scrollY"];
    router.replace(router, undefined, { shallow: true });
  }, [router, post.content]);

  useEffect(() => {
    if (post.paywallEnabled) {
      // In some scenarios where we go from post listing to detail, we receive post without content.
      // As a hotfix, we check for content to prevent errors.
      const newShowPaywall = post.content?.includes("remp_content_locked") ?? false;

      setShowPaywall(newShowPaywall);
      setIsUnlocked(!newShowPaywall);
    } else {
      setShowPaywall(false);
      setIsUnlocked(false);
    }
  }, [post]);

  useEffect(() => {
    if (post.paywallEnabled && router.query.ast) {
      if (user === undefined) {
        setIsLoginDialogVisible(post.paywallEnabled ? true : false);
        return;
      }
      providePostContent(undefined, router.query.ast as string);
    } else if (
      post.paywallEnabled &&
      router.query.hash &&
      (!document.referrer || document.referrer.includes("localhost") || document.referrer.includes("seznam.cz"))
    ) {
      providePostContent(router.query.hash as string);
    } else if (user && !subscriptions?.length && user.user_meta.unlocked_posts?.includes(post.id.toString())) {
      providePostContent().then(() => {
        setShowPaywall(false);
        setIsUnlocked(true);
      });
    }
  }, [providePostContent, post, router, user, subscriptions]);

  const unlockContent = async () => {
    store.dispatch(unlockPost(post.id));
    setIsUnlocked(true);
    // TODO: This should be here as well, but it is better to use isLoadingArticle instead
    // setShowPaywall(false);
    setIsLoadingArticle(true);
    await providePostContent();
    setIsLoadingArticle(false);
    pushToDataLayer({
      event: "article_interaction",
      interaction_type: "click_cta",
      interaction_option: "Odemknout",
    });
  };

  return (
    <>
      <div className="touch-pan-y pb-2">
        <PostContentHead post={post} />
        <PostSubjectIntroducing
          perex={post?.postDetails?.pageHeaderPerex}
          customTitle={post?.postDetails?.pageHeaderCustomTitle}
          image={post?.postDetails?.pageHeaderImage}
          imageCaption={post?.postDetails?.pageHeaderImageCaption}
          imageSource={post?.postDetails?.pageHeaderSource}
          imageAuthor={post?.postDetails?.pageHeaderAuthor}
          galleryCount={post?.galleryCount || 0}
          hasGallery={post?.hasGallery}
          postId={post.id}
          postSlug={post.slug}
          allImageSources={allImageSources}
        />
        <div className={`${styles.postContentContainer} ${styles.firstLetter}`}>{parsedContent}</div>
        {isLoadingArticle && (
          <div className="flex items-center justify-center p-4 text-orange">
            <Loader />
          </div>
        )}
        {!isLoadingArticle && showPaywall && !isUnlocked && (
          <PostPaywall onUnlockContent={unlockContent} postId={post.id} />
        )}
        <div className={styles.postNotesContainer}>
          <Text>{post.articleNotes}</Text>
        </div>
      </div>
      <div className={`${isUserLoggedInAndUnlocked ? "mb-3.5" : "mb-0"}`}>
        {/*TODO: Waiting for subscripton plans*/}
        {/*<PostLowerSubscriptionPaywall />*/}
        {isUserLoggedInAndUnlocked && <PostUnlockedPaywall postId={post.id} />}
      </div>
      <ReporterSocials reporterSupportDescription="Podpořte Reportér sdílením článku" post={post} />
      <SuccessDialogModalWindow isOpen={isLoginDialogVisible} onClose={() => setIsLoginDialogVisible(false)} />
    </>
  );
};
